import { Component, Input, Output, AfterViewInit, OnInit, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { SearchItem } from '../../models/index';
import { authActions, Auth, authSelectors } from '@content-platform/web-angular-shared-util-auth';
import {
  Application,
  userPermissionListSelectors,
} from '@content-platform/web/angular/shared/data-access/application-api';
import { LoggerService } from '@content-platform/web-angular-shared-util-logging';

import { UserMenuItem } from '../user-menu/user-menu-item';

/**
 * Navigation bar container appears on the top of the page. This comprises of components which appear in the
 * header area.
 *
 * @export
 * @class NavBarComponent
 */
@Component({
  selector: 'app-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
})
export class NavBarComponent implements AfterViewInit, OnInit {
  @Input() logo = 'assets/logo.png';
  @Input() appTitle: string;
  @Input() subTitle: string;
  @Input() additionalMenuItems: UserMenuItem[];
  @Input() isAppMenuShow = false;
  @Input() searchFilter?: string[] = [];
  @Output() searchFilterSelected?: EventEmitter<string[]> = new EventEmitter();
  @Output() search?: EventEmitter<string> = new EventEmitter();
  @Output() searchItemSelected?: EventEmitter<string[]> = new EventEmitter();
  @Input() searchResults?: Observable<SearchItem[]>;
  @Input() searchCorrectionResults?: Observable<SearchItem[]>;
  @Input() searchHasTags?: boolean;
  @Input() highlightSearch: boolean;
  @Output() clearSearch?: EventEmitter<boolean> = new EventEmitter();
  @Input() searchPlaceHolderText?: string = 'Search here...';
  @Input() floatPlaceHolder?: string = 'auto';
  @Input() maintenanceMessage?: string;
  @Input() showMaintenanceMessage?: boolean;

  searchCtrl = new FormControl();

  previousFilterOption = ['All'];
  selectedFilterOption = ['All'];

  user$: Observable<adal.User>;
  applications$: Observable<Application[]>;
  isLoggedIn$: Observable<boolean>;
  isSearchEnabled = false;
  private logger: LoggerService;

  /**
   * Creates an instance of NavBarComponent.
   * @param {Store<Auth>} store
   * @memberof NavBarComponent
   */
  constructor(private store: Store<Auth>, loggerService: LoggerService, public router: Router) {
    this.user$ = store.pipe(select(authSelectors.getADUser));
    this.applications$ = this.store.pipe(
      select(userPermissionListSelectors.getDisplayableUserApplications)
    );

    this.isLoggedIn$ = this.store.pipe(select(authSelectors.getIsLoggedIn));
    this.logger = loggerService.instance('NavBarComponent');
  }

  ngOnInit() {
    // EventEmitters are Observables and we can check if it has subscribers this way
    this.isSearchEnabled = this.search.observers.length > 0;
  }

  ngAfterViewInit(): void {
    // Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    this.searchCtrl.valueChanges.subscribe((val) => {
      this.search.emit(val);

      if (val === '' || val === ' ') {
        this.searchCtrl.reset();
      }
    });
  }

  /**
   * logs out the user from the content-platform
   *
   * @memberof NavBarComponent
   */
  logout(): void {
    this.store
      .select(authSelectors.getAuthenticationStrategy)
      .subscribe((authStrategy) => this.store.dispatch(new authActions.Logout(authStrategy)));
  }

  switchApp(app: Application): void {
    if (app) {
      this.logger.log('Switch App', {
        appName: app.name,
      });
      window.location.href = app.url;
    }
  }

  filterOptionSelected(option) {
    const selection = option.value;
    if (
      selection.length === 0 ||
      selection.length === this.searchFilter.length ||
      (selection.includes('All') && !this.previousFilterOption.includes('All'))
    ) {
      this.selectedFilterOption = ['All'];
    } else if (selection.length > 1 && this.previousFilterOption.includes('All')) {
      this.selectedFilterOption = selection.splice(1, 1);
    }
    this.previousFilterOption = this.selectedFilterOption;
    this.searchFilterSelected.emit(this.selectedFilterOption);
  }

  optionSelected(option) {
    this.searchCtrl.reset();
    this.searchItemSelected.emit(option);
  }

  displayFn(item?: any): string | undefined {
    return item ? item : '';
  }

  clearSearchText(softClear?: boolean) {
    this.searchCtrl.reset();
    if (!softClear) {
      this.clearSearch.emit(true);
    }
  }

  setSearchText(text: string) {
    this.searchCtrl.setValue(text);
  }
}
